import { useCallback, useState } from "react";
import { api } from "../api/client";

/** Triggers a browser download for a JSON payload. */
function saveJson(filename: string, data: unknown): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();

  // The anchor has already captured the data; leaving the URL alive would pin
  // the whole payload in memory for the life of the page.
  URL.revokeObjectURL(url);
}

/**
 * Downloads created flows, either all together or one at a time.
 *
 * Fetched on demand rather than returned with the migration: flow bodies are
 * large, and most runs are never inspected at that level.
 *
 * `busy` is the id being fetched, or "__all__", so each button can show its
 * own progress without a second click starting a duplicate request.
 */
export function useFlowDownload(botName: string | null) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const folder = botName ?? undefined;

  const run = useCallback(
    async (flowIds: string[], key: string, filename: string) => {
      if (!flowIds.length || busy) return;
      setBusy(key);
      setError(null);

      try {
        const data = await api.flows(flowIds, folder);

        // A single flow downloads as the flow itself, not wrapped in a
        // bundle - that is the shape CX Designer exports and `fixtures/` expects.
        const single = flowIds.length === 1 ? data.flows[0] : undefined;
        saveJson(filename, single?.flow ?? data);

        // A partial export still downloads; the file names what failed.
        if (data.error) setError(data.error);
        else if (single && !single.flow) setError(single.error ?? "Not found.");
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [busy, folder],
  );

  return {
    error,
    busy,
    downloadAll: (flowIds: string[]) =>
      run(flowIds, "__all__", `${botName ?? "migration"}-flows.json`),
    downloadOne: (flowId: string) =>
      run([flowId], flowId, `${flowId}.flow.json`),
  };
}
